import { ConfigProvider, Switch, Tooltip } from 'antd';
import type { ThemeConfig } from 'antd';
import { ClockCircleOutlined, LockOutlined, RightOutlined } from '@ant-design/icons';
import { colors } from 'theme/tokens';
import {
  PermissionAction,
  PermissionCell,
  PermissionGroup,
  PermissionMatrixView,
  PermissionScope,
  PermissionSlot,
  PermissionTier,
  Scope,
  Tier,
} from './types';
import styles from './styles.module.scss';

export const cellKey = (tier: Tier, action: string, scope: Scope) => `${tier}|${action}|${scope}`;

export const NOT_ENFORCED_TIP =
  'Việc này chưa được kiểm theo bảng quyền. Công tắc sẽ có tác dụng khi phần kiểm được bật.';
const DEFAULT_LOCK_TIP = 'Ô này bị khoá theo luật cố định, không chỉnh được.';
/** Nhãn công tắc của việc không chia theo người được xử lý (`singleSwitch`). */
const SINGLE_LABEL = 'Được làm';

export const onOff = (value: boolean) => (value ? 'Bật' : 'Tắt');

/**
 * Chỉ áp trong bảng quyền. Công tắc đang TẮT của antd có nền xám nhạt rgba(0,0,0,.25): chữ "Tắt"
 * màu trắng trên đó chỉ ~1,9:1. Đổi nền sang textSecondary (5,8:1), rê chuột sang inkSoft.
 * Công tắc bị vô hiệu (ô khoá, tài khoản chỉ xem) mờ 0,8 thay vì 0,65 để vẫn đọc được trạng thái.
 */
const MATRIX_THEME: ThemeConfig = {
  token: {
    colorTextQuaternary: colors.textSecondary,
    colorTextTertiary: colors.inkSoft,
    opacityLoading: 0.8,
  },
};

/** Ô của một cấp × một nhóm người được xử lý; thiếu dữ liệu thì coi như ô khoá, đang tắt. */
export function readCell(action: PermissionAction, tier: Tier, scope: Scope): PermissionCell {
  const cell = action.cells?.[tier]?.[scope];
  if (cell) return cell;
  return { allowed: false, defaultAllowed: false, locked: true };
}

/**
 * Các công tắc trong một ô. Bình thường mỗi nhóm người được xử lý một công tắc; việc
 * `singleSwitch` chỉ có MỘT công tắc điều khiển mọi nhóm (máy chủ cũng đổi cả hai cùng lúc).
 */
export function slotsOf(action: PermissionAction, scopes: PermissionScope[]): PermissionSlot[] {
  if (action.singleSwitch && scopes.length > 0) {
    return [{ key: 'ALL', label: SINGLE_LABEL, scopes: scopes.map((scope) => scope.key) }];
  }
  return scopes.map((scope) => ({ key: scope.key, label: scope.label, scopes: [scope.key] }));
}

/** Trạng thái của một công tắc sau khi chồng bản nháp lên dữ liệu máy chủ. */
export function readSlot(
  action: PermissionAction,
  tier: Tier,
  slot: PermissionSlot,
  draft: Record<string, boolean>
) {
  const cells = slot.scopes.map((scope) => readCell(action, tier, scope));
  const keys = slot.scopes.map((scope) => cellKey(tier, action.action, scope));
  const first = cells[0];
  const draftKey = keys.find((key) => key in draft);
  const saved = first.allowed;
  const value = draftKey ? draft[draftKey] : saved;
  const locked = cells.some((cell) => cell.locked);
  return {
    keys,
    saved,
    value,
    locked,
    lockReason: cells.find((cell) => cell.locked)?.lockReason,
    dirty: !locked && value !== saved,
    differs: value !== first.defaultAllowed,
  };
}

interface SlotSwitchProps {
  tier: PermissionTier;
  slot: PermissionSlot;
  action: PermissionAction;
  draft: Record<string, boolean>;
  editable: boolean;
  onToggle: (next: boolean) => void;
}

const SlotSwitch = ({ tier, slot, action, draft, editable, onToggle }: SlotSwitchProps) => {
  const state = readSlot(action, tier.key, slot, draft);
  const single = slot.scopes.length > 1;
  // Tên đọc được luôn chứa chữ đang hiện cạnh công tắc ("Được làm" / tên nhóm người), để người
  // điều khiển bằng giọng nói gọi đúng tên nhìn thấy.
  const name = single
    ? `${slot.label}: ${tier.label} · ${action.label}`
    : `${tier.label} · ${action.label} · ${slot.label}`;
  const lockTip = state.lockReason || DEFAULT_LOCK_TIP;

  let mark: React.ReactNode = null;
  if (state.locked) {
    // Lý do khoá hiện ở Tooltip bọc cả dòng (xem cuối hàm): rê chuột lên chữ, ổ khoá hay công tắc,
    // hoặc Tab tới ổ khoá, đều thấy.
    mark = (
      <span
        className={styles.markSlot}
        tabIndex={0}
        role="img"
        aria-label={`${name}: đã khoá. ${lockTip}`}
      >
        <LockOutlined aria-hidden />
      </span>
    );
  } else if (state.differs) {
    mark = (
      <Tooltip title="Khác mặc định">
        <span className={styles.markSlot} role="img" aria-label="Khác mặc định">
          <span className={styles.dot} />
        </span>
      </Tooltip>
    );
  } else {
    mark = <span className={styles.markSlot} aria-hidden />;
  }

  const notes = [
    state.locked ? 'đã khoá' : '',
    !state.locked && state.differs ? 'khác mặc định' : '',
    state.dirty ? 'chưa lưu' : '',
  ].filter(Boolean);

  const id = `perm-${tier.key}-${action.action}-${slot.key}`;
  const canToggle = editable && !state.locked;
  const rowClass = [
    styles.scopeRow,
    state.dirty ? styles.scopeDirty : '',
    canToggle ? styles.scopeEditable : '',
  ]
    .filter(Boolean)
    .join(' ');

  const row = (
    <div className={rowClass} title={state.dirty ? 'Thay đổi chưa lưu' : undefined}>
      <label className={styles.scopeLabel} htmlFor={id}>
        {slot.label}
      </label>
      <span className={styles.scopeControl}>
        {mark}
        <Switch
          id={id}
          size="small"
          checked={state.value}
          disabled={!canToggle}
          checkedChildren="Bật"
          unCheckedChildren="Tắt"
          aria-label={notes.length ? `${name} (${notes.join(', ')})` : name}
          onChange={onToggle}
        />
      </span>
    </div>
  );

  // Công tắc bị vô hiệu không nhận chuột / Tab, nên Tooltip bọc cả dòng và mở cả khi focus
  // (Tab tới ổ khoá), không chỉ khi rê chuột.
  return state.locked ? (
    <Tooltip title={lockTip} trigger={['hover', 'focus']}>
      {row}
    </Tooltip>
  ) : (
    row
  );
};

interface MatrixProps {
  view: PermissionMatrixView;
  draft: Record<string, boolean>;
  editable: boolean;
  collapsed: Record<string, boolean>;
  onToggleGroup: (key: string) => void;
  onChange: (tier: Tier, action: PermissionAction, slot: PermissionSlot, next: boolean) => void;
}

const countDiffers = (
  group: PermissionGroup,
  tiers: PermissionTier[],
  scopes: PermissionScope[],
  draft: Record<string, boolean>
) => {
  let differs = 0;
  let dirty = 0;
  group.actions.forEach((action) => {
    const slots = slotsOf(action, scopes);
    tiers.forEach((tier) => {
      slots.forEach((slot) => {
        const state = readSlot(action, tier.key, slot, draft);
        if (state.differs) differs += 1;
        if (state.dirty) dirty += 1;
      });
    });
  });
  return { differs, dirty };
};

/**
 * Bảng quyền: hàng = việc (gom theo mảng, thu gọn được), cột = 3 cấp quản lý, mỗi ô 2 công tắc.
 * Dùng <table> thật để trình đọc màn hình đọc được tên hàng / tên cột.
 */
const Matrix = (props: MatrixProps) => {
  return (
    <div
      className={styles.matrixWrap}
      role="region"
      aria-label="Bảng quyền theo cấp quản lý"
      tabIndex={0}
    >
      <ConfigProvider theme={MATRIX_THEME}>
        <MatrixTable {...props} />
      </ConfigProvider>
    </div>
  );
};

const MatrixTable = ({
  view,
  draft,
  editable,
  collapsed,
  onToggleGroup,
  onChange,
}: MatrixProps) => {
  const { tiers, scopes, groups } = view;
  const lastGroup = groups[groups.length - 1];

  return (
    <table className={styles.matrix}>
      <caption className={styles.srOnly}>
        Bảng quyền: mỗi hàng là một việc, mỗi cột là một cấp quản lý, mỗi ô có công tắc theo người
        được xử lý.
      </caption>
      <colgroup>
        <col className={styles.colAction} />
        {tiers.map((tier) => (
          <col key={tier.key} className={styles.colTier} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th scope="col" className={`${styles.headCell} ${styles.headFirst}`}>
            Việc
          </th>
          {tiers.map((tier) => (
            <th key={tier.key} scope="col" className={styles.headCell}>
              {tier.label}
            </th>
          ))}
        </tr>
      </thead>
      {groups.map((group) => {
        const isCollapsed = !!collapsed[group.key];
        const isLastGroup = group === lastGroup;
        const { differs, dirty } = countDiffers(group, tiers, scopes, draft);
        const panelId = `perm-group-${group.key}`;
        return (
          <tbody key={group.key} id={panelId}>
            <tr className={isLastGroup && isCollapsed ? styles.lastRow : undefined}>
              <th colSpan={tiers.length + 1} scope="colgroup" className={styles.sectionCell}>
                <button
                  type="button"
                  className={styles.sectionButton}
                  aria-expanded={!isCollapsed}
                  aria-controls={panelId}
                  onClick={() => onToggleGroup(group.key)}
                >
                  <RightOutlined
                    className={
                      isCollapsed
                        ? styles.sectionChevron
                        : `${styles.sectionChevron} ${styles.sectionChevronOpen}`
                    }
                    aria-hidden
                  />
                  <span className={styles.sectionLabel}>{group.label}</span>
                  <span className={styles.sectionMeta}>
                    {group.actions.length} việc
                    {differs > 0 ? ` · ${differs} ô khác mặc định` : ''}
                    {dirty > 0 ? ` · ${dirty} chưa lưu` : ''}
                  </span>
                </button>
              </th>
            </tr>
            {!isCollapsed &&
              group.actions.map((action, index) => {
                const isLastRow = isLastGroup && index === group.actions.length - 1;
                const slots = slotsOf(action, scopes);
                return (
                  <tr key={action.action} className={isLastRow ? styles.lastRow : undefined}>
                    <th scope="row" className={styles.actionCell}>
                      <div className={styles.actionLabel}>
                        <span>{action.label}</span>
                        {action.enforced === false && (
                          <Tooltip title={NOT_ENFORCED_TIP} trigger={['hover', 'focus']}>
                            <span className={styles.notEnforced} tabIndex={0}>
                              <ClockCircleOutlined aria-hidden />
                              Chưa có hiệu lực
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      {action.hint && <div className={styles.actionHint}>{action.hint}</div>}
                    </th>
                    {tiers.map((tier) => (
                      <td key={tier.key} className={styles.cell}>
                        {slots.map((slot) => (
                          <SlotSwitch
                            key={slot.key}
                            tier={tier}
                            slot={slot}
                            action={action}
                            draft={draft}
                            editable={editable}
                            onToggle={(next) => onChange(tier.key, action, slot, next)}
                          />
                        ))}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        );
      })}
    </table>
  );
};

export default Matrix;
